import axios from 'axios';
import { TrendingUp, Clock, Layers, ArrowUpRight, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Legend,
    CartesianGrid,
} from 'recharts';

import { notifyUser } from "../utils/notifyUser";

function InvestorDashboard({ user }) {
    const [, setFormData] = useState(null);
    const [refreshFlag] = useState(false);
    const [investmentStats, setInvestmentStats] = useState(null);
    const [isStatsLoading, setIsStatsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('investments');
    const navigate = useNavigate();

    const [campaigns, setCampaigns] = useState([]);
    const [startIdx, setStartIdx] = useState(0);
    const [selectedIdx, setSelectedIdx] = useState(0);

    const AQI_PRIMARY = 'var(--navy)';
    const AQI_SECONDARY = '#C2D7E2';
    const AQI_CHART_COLORS = [AQI_PRIMARY, '#2F6F95', '#5B8FB3', '#8FB7CF', AQI_SECONDARY];

    const fetchInvestorData = async () => {
        const email = user?.email;
        if(!email) {
            notifyUser(null, "error", 'User email not found.', "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                {
                    params: { email },
                    withCredentials: true,
                }
            );
            setFormData(res.data);
        } catch(err) {
            console.error('Failed to fetch investor data:', err);
            notifyUser(null, "error", 'Failed to load investor data', "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Fetch investment stats
    const fetchInvestmentStats = async () => {
        const investorId = user?.id;
        if(!investorId) {
            setInvestmentStats(null);
            setIsStatsLoading(false);
            return;
        }

        setIsStatsLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/investor/${investorId}/investment-stats`
            );
            setInvestmentStats(res.data);
        } catch(err) {
            setInvestmentStats(null);
            console.error('Failed to fetch investment stats:', err);
        } finally {
            setIsStatsLoading(false);
        }
    };

    useEffect(() => {
        fetchInvestorData();
        fetchInvestmentStats();
    }, [refreshFlag]);

    useEffect(() => {
        if(user?.id) {
            fetchInvestmentStats();
        }
    }, [user]);

    // Fetch public campaigns for carousel
    useEffect(() => {
        let mounted = true;
        const fetchCampaigns = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public-campaigns`);
                const data = res.data || [];
                if (!mounted) return;
                const count = data.length >= 5 ? 5 : data.length;
                const shuffled = data.length >= 5 ? shuffleArray(data).slice(0, 5) : data;
                setCampaigns(shuffled);
                setSelectedIdx(Math.floor(Math.min(count, 5) / 2));
            } catch (err) {
                console.error('Error fetching campaigns', err);
                if (!mounted) return;
                setCampaigns([]);
            }
        };

        fetchCampaigns();

        return () => {
            mounted = false;
        };
    }, []);

    function shuffleArray(array) {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }

    const handleGoToInvestments = () => {
        navigate('/investments');
    };

    const hasInvestments = (investmentStats?.total_investments ?? 0) > 0;

    const renderCampaignCards = () => {
        if(campaigns.length === 0) {
            return <div className='text-center text-gray-500'>There are no campaigns available to invest in at this time..</div>;
        }

        return (
            <div className='max-w-4xl mx-auto flex items-center gap-4'>
                <button
                    onClick={() => {
                        setStartIdx((idx) => (idx - 1 + campaigns.length) % campaigns.length);
                        setSelectedIdx((idx) => (idx - 1 + campaigns.length) % campaigns.length);
                    }}
                    className='w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center relative z-50'
                    aria-label='Previous'
                >
                    <ChevronLeft className='h-5 w-5' />
                </button>

                <div className='flex-1 overflow-visible'>
                    <div className='relative flex justify-center gap-4 px-4'>
                        {(() => {
                            const visible = [];
                            const count = Math.min(5, campaigns.length);
                            for (let i = 0; i < count; i++) {
                                const idx = (startIdx + i) % campaigns.length;
                                visible.push({ campaign: campaigns[idx], idx });
                            }

                            return visible.map((item, i) => {
                                const center = Math.floor(visible.length / 2);
                                const pos = i - center;
                                let scale = 1;
                                let z = 10;
                                let opacity = 1;
                                let translateY = 0;

                                if (pos === 0) {
                                    scale = 1.15;
                                    z = 30;
                                } else if (Math.abs(pos) === 1) {
                                    scale = 0.95;
                                    z = 20;
                                    opacity = 0.85;
                                    translateY = 20;
                                } else {
                                    scale = 0.8;
                                    opacity = 0.6;
                                    translateY = 40;
                                }

                                const isSelected = (startIdx + i) % campaigns.length === selectedIdx;

                                return (
                                    <div
                                        key={item.campaign.id}
                                        onClick={() => navigate(`/campaigns/view/${item.campaign.id}`)}
                                        className={`flex-shrink-0 rounded-2xl cursor-pointer bg-white transition-all duration-300 ${isSelected ? 'ring-4 ring-navy-2 shadow-2xl' : 'shadow-md'}`}
                                        style={{
                                            width: 200,
                                            height: 280,
                                            marginLeft: i === 0 ? 0 : -40,
                                            marginRight: i === visible.length - 1 ? 0 : -40,
                                            transform: `scale(${scale}) translateY(${translateY}px)`,
                                            zIndex: z,
                                            opacity,
                                        }}
                                        tabIndex={0}
                                    >
                                        <img
                                            src={item.campaign.companyLogo || item.campaign.campaign_background_image}
                                            alt={item.campaign.campaign_name}
                                            className='object-cover w-full h-full rounded-2xl'
                                        />
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>

                <button
                    onClick={() => {
                        setStartIdx((idx) => (idx + 1) % campaigns.length);
                        setSelectedIdx((idx) => (idx + 1) % campaigns.length);
                    }}
                    className='w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center relative z-50'
                    aria-label='Next'
                >
                    <ChevronRight className='h-5 w-5' />
                </button>
            </div>
        );
    };

    const renderCampaignSection = ({ showEmptyCopy = false } = {}) => (
        <div className='mt-6'>
            {showEmptyCopy ? (
                <>
                    <h2 className='text-xl font-semibold text-gray-800 mb-4 text-center'>No Investments Yet</h2>
                    <p className='text-center text-gray-600 mb-12'>
                        You haven't made any investments yet. Explore available campaigns below and click a card to view details.
                    </p>
                </>
            ) : (
                <div className='mb-12 text-center'>
                    <h2 className='text-xl font-semibold text-gray-800'>Available Campaigns</h2>
                    <p className='mt-2 text-gray-600'>
                        Browse current campaigns and open any card to review the details.
                    </p>
                </div>
            )}

            {renderCampaignCards()}
        </div>
    );

    return (
        <div className='min-h-screen bg-white p-6 font-poppins'>
            {/* Welcome Section */}
            <div className='mb-8'>
                <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                    {/* subtle brand tint */}
                    <div
                        className='absolute inset-0'
                        style={{
                            background: `linear-gradient(135deg, #C2D7E255 0%, #ffffff 55%)`,
                        }}
                    />

                    <div className='relative p-6'>
                        {/* Left */}
                        <div className='min-w-0'>
                            <h1 className='text-2xl font-bold tracking-tight text-gray-900'>
                                Welcome,{' '}
                                <span style={{ color: 'var(--navy)' }}>{user?.first_name}</span>!
                            </h1>

                            <p className='mt-1 text-sm text-gray-500'>
                                <span className='font-medium text-gray-700'>{user?.email}</span>
                                <span className='mx-2 text-gray-300'>•</span>
                                <span>{new Date().toLocaleString()}</span>
                            </p>

                            {/* Role badge */}
                            <div className='mt-3'>
                                <span
                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1`}
                                    style={
                                        user?.role === 'admin'
                                            ? {
                                                  backgroundColor: '#FEE2E2',
                                                  color: '#B91C1C',
                                                  borderColor: '#FCA5A5',
                                              }
                                            : {
                                                  backgroundColor: '#C2D7E2',
                                                  color: 'var(--navy)',
                                                  borderColor: '#C2D7E2',
                                              }
                                    }
                                >
                                    {user?.role ? user.role.toUpperCase() : 'USER'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {hasInvestments && (
                <div className='mb-6 border-b border-gray-200'>
                    <div className='flex gap-2'>
                        <button
                            type='button'
                            onClick={() => setActiveTab('investments')}
                            className={`rounded-t-xl px-5 py-3 text-sm font-semibold transition-colors duration-200 ${activeTab === 'investments' ? 'bg-navy text-white' : 'bg-[#EAF1F5] text-navy hover:bg-[#D7E5EC]'}`}
                        >
                            Investments
                        </button>
                        <button
                            type='button'
                            onClick={() => setActiveTab('campaigns')}
                            className={`rounded-t-xl px-5 py-3 text-sm font-semibold transition-colors duration-200 ${activeTab === 'campaigns' ? 'bg-navy text-white' : 'bg-[#EAF1F5] text-navy hover:bg-[#D7E5EC]'}`}
                        >
                            Campaigns
                        </button>
                    </div>
                </div>
            )}

            {/* Investment Statistics */}
            {hasInvestments && activeTab === 'investments' && (
                <section className='space-y-6'>
                    {/* Top Stats */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5'>
                        {/* Total Invested */}
                        <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div
                                className='absolute inset-0'
                                style={{
                                    background: `linear-gradient(135deg, #5B8FB355 0%, #ffffff 60%)`,
                                }}
                            />
                            <div className='relative p-6'>
                                <div className='flex items-start justify-between'>
                                    <div>
                                        <p className='text-sm font-medium text-gray-600'>
                                            Total Invested
                                        </p>
                                        <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900'>
                                            $
                                            {investmentStats.total_invested?.toLocaleString(
                                                undefined,
                                                {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }
                                            )}
                                        </p>
                                        <p className='mt-2 text-xs text-gray-500'>
                                            All-time invested amount
                                        </p>
                                    </div>

                                    <div
                                        className='flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm'
                                        style={{ backgroundColor: '#2F6F95' }}
                                    >
                                        <TrendingUp className='h-5 w-5' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Total Investments */}
                        <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div
                                className='absolute inset-0'
                                style={{
                                    background: `linear-gradient(135deg, ${AQI_SECONDARY}55 0%, #ffffff 60%)`,
                                }}
                            />
                            <div className='relative p-6'>
                                <div className='flex items-start justify-between'>
                                    <div>
                                        <p className='text-sm font-medium text-gray-600'>
                                            Total Investments
                                        </p>
                                        <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900'>
                                            {investmentStats.total_investments ?? 0}
                                        </p>
                                        <p className='mt-2 text-xs text-gray-500'>
                                            Count of all investments
                                        </p>
                                    </div>

                                    <div
                                        className='flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm'
                                        style={{ backgroundColor: AQI_PRIMARY }}
                                    >
                                        <Layers className='h-5 w-5' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pending Investments */}
                        <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div
                                className='absolute inset-0'
                                style={{
                                    background: `linear-gradient(135deg, #FEF3C755 0%, #ffffff 60%)`,
                                }}
                            />
                            <div className='relative p-6'>
                                <div className='flex items-start justify-between'>
                                    <div>
                                        <p className='text-sm font-medium text-gray-600'>
                                            Pending Investments
                                        </p>
                                        <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900'>
                                            {investmentStats.pending_investments ?? 0}
                                        </p>
                                        <p className='mt-2 text-xs text-gray-500'>
                                            Awaiting completion
                                        </p>
                                    </div>

                                    <div
                                        className='flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm'
                                        style={{ backgroundColor: '#EAB308' }}
                                    >
                                        <Clock className='h-5 w-5' />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cancelled Investments */}
                        <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div
                                className='absolute inset-0'
                                style={{
                                    background: `linear-gradient(135deg, #FEE2E255 0%, #ffffff 60%)`,
                                }}
                            />
                            <div className='relative p-6'>
                                <div className='flex items-start justify-between'>
                                    <div>
                                        <p className='text-sm font-medium text-gray-600'>
                                            Cancelled Investments
                                        </p>
                                        <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900'>
                                            {investmentStats.cancelled_investments ?? 0}
                                        </p>
                                        <p className='mt-2 text-xs text-gray-500'>
                                            {(investmentStats.cancelled_investments ?? 0) > 0
                                                ? 'Review cancelled investments'
                                                : 'No cancelled investments'}
                                        </p>
                                    </div>

                                    <div
                                        className='flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm'
                                        style={{
                                            backgroundColor:
                                                (investmentStats.cancelled_investments ?? 0) > 0
                                                    ? '#DC2626'
                                                    : '#9CA3AF',
                                        }}
                                    >
                                        <XCircle className='h-5 w-5' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
                        {/* Status Breakdown */}
                        <div className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div className='px-6 pt-6'>
                                <h2
                                    className='text-base font-semibold'
                                    style={{ color: AQI_PRIMARY }}
                                >
                                    Investment Status Breakdown
                                </h2>
                                <p className='mt-1 text-sm text-gray-500'>
                                    Distribution of your investments by status
                                </p>
                            </div>

                            <div className='px-6 pb-6 pt-4'>
                                <ResponsiveContainer width='100%' height={280}>
                                    <PieChart>
                                        <Pie
                                            data={Object.entries(
                                                investmentStats.status_breakdown || {}
                                            ).map(([status, value]) => ({
                                                name: status,
                                                value,
                                            }))}
                                            dataKey='value'
                                            nameKey='name'
                                            cx='50%'
                                            cy='50%'
                                            outerRadius={95}
                                            innerRadius={60}
                                            paddingAngle={2}
                                            labelLine={false}
                                            label={({ percent }) =>
                                                `${(percent * 100).toFixed(0)}%`
                                            }
                                        >
                                            {Object.entries(
                                                investmentStats.status_breakdown || {}
                                            ).map((entry, idx) => (
                                                <Cell
                                                    key={entry[0]}
                                                    fill={
                                                        AQI_CHART_COLORS[
                                                            idx % AQI_CHART_COLORS.length
                                                        ]
                                                    }
                                                />
                                            ))}
                                        </Pie>

                                        <Tooltip
                                            formatter={(value, name) => [value, name]}
                                            contentStyle={{
                                                borderRadius: '10px',
                                                border: '1px solid #E5E7EB',
                                                fontSize: '13px',
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Top Campaigns by Amount */}
                        <div className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
                            <div className='px-6 pt-6'>
                                <h2
                                    className='text-base font-semibold'
                                    style={{ color: AQI_PRIMARY }}
                                >
                                    Top Campaigns by Investment Amount
                                </h2>
                                <p className='mt-1 text-sm text-gray-500'>
                                    Campaigns with highest total investments
                                </p>
                            </div>

                            <div className='px-6 pb-6 pt-4'>
                                <ResponsiveContainer width='100%' height={280}>
                                    <BarChart
                                        data={investmentStats.top_campaigns_by_amount || []}
                                        barSize={28}
                                    >
                                        <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                                        <XAxis
                                            dataKey='campaign_name'
                                            tick={{
                                                fontSize: 12,
                                                fill: '#4B5563',
                                            }}
                                        />
                                        <YAxis
                                            tick={{
                                                fontSize: 12,
                                                fill: '#4B5563',
                                            }}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: '10px',
                                                border: '1px solid #E5E7EB',
                                                fontSize: '13px',
                                            }}
                                            formatter={(value) =>
                                                `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                            }
                                        />
                                        <Legend />
                                        <Bar
                                            dataKey='total_amount'
                                            name='Total Invested'
                                            fill={AQI_PRIMARY}
                                            radius={[10, 10, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Recent Investments */}
                    <div className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
                        <div className='flex items-center justify-between px-6 pt-6'>
                            <div>
                                <h2
                                    className='text-base font-semibold'
                                    style={{ color: AQI_PRIMARY }}
                                >
                                    Recent Investments
                                </h2>
                                <p className='mt-1 text-sm text-gray-500'>Latest activity</p>
                            </div>

                            <div
                                className='inline-flex items-center gap-2 text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity'
                                style={{ color: AQI_PRIMARY }}
                                onClick={handleGoToInvestments}
                            >
                                View all <ArrowUpRight className='h-4 w-4' />
                            </div>
                        </div>

                        <div className='px-6 pb-6 pt-4 overflow-x-auto'>
                            <table className='min-w-full text-sm'>
                                <thead>
                                    <tr className='text-left text-gray-500'>
                                        <th className='py-3 pr-4 font-medium'>Date</th>
                                        <th className='py-3 pr-4 font-medium text-right'>Amount</th>
                                        <th className='py-3 pr-4 font-medium'>Campaign</th>
                                        <th className='py-3 pr-4 font-medium'>Status</th>
                                    </tr>
                                </thead>

                                <tbody className='divide-y divide-gray-100'>
                                    {(investmentStats.recent_investments || []).map((inv, idx) => {
                                        const status = (inv.status || '').toLowerCase();

                                        // AQI-themed status pills
                                        const badge = status.includes('pending')
                                            ? 'bg-[#C2D7E2] text-navy ring-[#C2D7E2]'
                                            : status.includes('approved') ||
                                                status.includes('completed') ||
                                                status.includes('success')
                                              ? 'bg-navy text-white ring-navy'
                                              : status.includes('rejected') ||
                                                  status.includes('failed') ||
                                                  status.includes('cancelled')
                                                ? 'bg-red-100 text-red-700 ring-red-200'
                                                : 'bg-gray-50 text-gray-700 ring-gray-200';

                                        return (
                                            <tr key={idx} className='hover:bg-gray-50/60'>
                                                <td className='py-3 pr-4 text-gray-700'>
                                                    {inv.date
                                                        ? new Date(inv.date).toLocaleDateString()
                                                        : ''}
                                                </td>

                                                <td className='py-3 pr-4 text-right font-semibold text-gray-900'>
                                                    $
                                                    {inv.amount?.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>

                                                <td className='py-3 pr-4 text-gray-700'>
                                                    <span className='font-medium text-gray-900'>
                                                        {inv.campaign_name}
                                                    </span>
                                                </td>

                                                <td className='py-3 pr-4'>
                                                    <span
                                                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${badge}`}
                                                    >
                                                        {inv.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {!isStatsLoading && !hasInvestments ? renderCampaignSection({ showEmptyCopy: true }) : null}
            {hasInvestments && activeTab === 'campaigns' ? renderCampaignSection() : null}
        </div>
    );
}

InvestorDashboard.propTypes = {
    user: PropTypes.shape({
        email: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        first_name: PropTypes.string,
        role: PropTypes.string,
    }),
};
export default InvestorDashboard;
